import { CreativeTypes } from './types';
import { MULTI_VALUED_FIELDS } from './creativesFormattingUtils';

const DEFAULT_CREATIVE_SIZE = 234;

export const OPTIONAL_CREATIVES_SIZES = [
    { width: DEFAULT_CREATIVE_SIZE, height: 234 },
    { width: DEFAULT_CREATIVE_SIZE, height: 410 },
    { width: DEFAULT_CREATIVE_SIZE, height: 180 },
    { width: DEFAULT_CREATIVE_SIZE, height: 120 },
];

const getClosestRatio = (currentRatio, ratios) => {
    return ratios.reduce((closest, ratio) => {
        return Math.abs(currentRatio - ratio) < Math.abs(currentRatio - closest) ? ratio : closest;
    });
};

export const getCreativeSizeRatio = (creativeWidth, creativeHeight, type) => {
    const optionalRatios = OPTIONAL_CREATIVES_SIZES.map(({ width, height }) => width / height);
    return !creativeWidth || !creativeHeight
        ? optionalRatios[type === CreativeTypes.PLAYABLE ? 1 : 0]
        : getClosestRatio(creativeWidth / creativeHeight, optionalRatios);
};

const calculateTotalTagsCount = creative => {
    const aiTagsCount = creative.aiTags?.reduce((sum, { tags }) => sum + (tags?.length || 0), 0) ?? 0;
    const multiValuedCount = MULTI_VALUED_FIELDS.reduce(
        (count, { aggregatedField, showMultiValuedTag }) =>
            count + (showMultiValuedTag && creative.multiValuedDimensions?.[aggregatedField]?.length > 0 ? 1 : 0),
        0
    );

    return aiTagsCount + multiValuedCount;
};

export const getMaxTagsCreative = creatives => {
    let maxTagsCount = 0;
    creatives.forEach(item => {
        const totalTagsCount = calculateTotalTagsCount(item);
        if (totalTagsCount > maxTagsCount) {
            maxTagsCount = totalTagsCount;
        }
    });
    return maxTagsCount;
};

const calculateSectionHeight = (sectionConfig, itemsCount) => {
    if (!itemsCount) return { calculatedHeight: 0, pureHeight: 0 };

    const rows = Math.min(Math.ceil(itemsCount / sectionConfig.maxItemsPerRow), sectionConfig.maxRows);
    return {
        pureHeight: rows * sectionConfig.itemHeight + (rows - 1) * sectionConfig.gap,
        calculatedHeight: sectionConfig.marginTop + rows * sectionConfig.itemHeight + (rows - 1) * sectionConfig.gap,
    };
};

const CARD_CONFIG = {
    padding: 16,
    asset: {
        height: 200,
    },
    tags: {
        itemHeight: 21,
        itemWidth: 100,
        gap: 8,
        maxRows: 2.5,
        marginTop: 8,
        maxItemsPerRow: 2,
    },
    metrics: {
        itemHeight: 17,
        itemWidth: 120,
        maxRows: 3.5,
        gap: 8,
        marginTop: 16,
        maxItemsPerRow: 2,
    },
    events: {
        itemHeight: 19,
        gap: 5,
        maxRows: 3.5,
        marginTop: 12,
        maxItemsPerRow: 1,
    },
    bottomRow: {
        height: 22,
        marginTop: 22,
    },
    adminSection: {
        height: 65,
        marginTop: 10,
    },
};

export const getCreativeSectionsSize = (creatives, selectedMetrics, selectedCohorts, isAdminMode) => {
    const tagsCount = getMaxTagsCreative(creatives);
    const tagsHeight = calculateSectionHeight(CARD_CONFIG.tags, tagsCount);
    const metricsHeight = calculateSectionHeight(CARD_CONFIG.metrics, selectedMetrics.length);
    const cohortsHeight = calculateSectionHeight(CARD_CONFIG.events, selectedCohorts.length);
    const adminSection = isAdminMode ? CARD_CONFIG.adminSection.height + CARD_CONFIG.adminSection.marginTop : 0;
    return {
        totalHeight:
            CARD_CONFIG.padding +
            CARD_CONFIG.asset.height +
            tagsHeight.calculatedHeight +
            metricsHeight.calculatedHeight +
            cohortsHeight.calculatedHeight +
            CARD_CONFIG.bottomRow.height +
            CARD_CONFIG.bottomRow.marginTop +
            adminSection,
        sectionHeights: {
            asset: CARD_CONFIG.asset.height,
            tags: tagsHeight.pureHeight,
            metrics: metricsHeight.pureHeight,
            events: cohortsHeight.pureHeight,
            bottomRow: CARD_CONFIG.bottomRow.height,
        },
    };
};
